import {
    Backdrop,
    Box,
    CircularProgress,
    Container,
    Fab,
    InputBase,
    MenuItem,
    Modal,
    Select,
    SelectChangeEvent
} from "@mui/material";
import TemplateCards from "../../components/TemplateCards.tsx";
import { ChangeEvent, useEffect, useState } from "react";
import ListIcon from '@mui/icons-material/List';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import TemplateList from "../../components/TemplateList.tsx";
import SearchIcon from '@mui/icons-material/Search';
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import TemplateStepper from "../../components/TemplateStepper/TemplateStepper";
import { TemplateMetadata, TemplateData } from "./types.ts";
import { useTranslation } from 'react-i18next';
import { api } from "../../api/api.ts";
import { useAuth } from "../../context/auth/useAuth.ts";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [direction, setDirection] = useState('card');
    const [templates, setTemplates] = useState<TemplateMetadata[]>([])
    const [blueprints, setBlueprints] = useState<TemplateMetadata[]>([]);
    const [tmpTemplates, setTmpTemplates] = useState<TemplateMetadata[]>([]);
    const [openTemplateStepper, setOpenTemplateStepper] = useState(false);
    const [initData, setInitData] = useState<TemplateData | null>(null);

    useEffect(() => {
        getTemplates().then();
    }, []);

    const getTemplates = async () => {
        setLoading(true);
        if (user && templates) {
            api.getTemplates(user?.subID)
                .then((templates) => {
                    const blueprints = templates.filter(template => template.blueprint === 1)
                        .sort((a, b) => (b.templateID === 166 ? 1 : 0) - (a.templateID === 166 ? 1 : 0));
                    const filteredTemplates = templates.filter(template => template.blueprint === 0);
                    setTemplates(filteredTemplates);
                    setBlueprints(blueprints);
                    setTmpTemplates(filteredTemplates);
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                    console.error('Error fetching templates');
                })
        }
    };

    const handleChange = (event: SelectChangeEvent) => {
        setDirection(event.target.value);
    };

    const handleSearch = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        try {
            const search = event.target.value.toString() ?? "";
            setTmpTemplates(templates.filter(function (item) {
                const { templateName } = item;
                if (templateName.toLowerCase().includes(search.toLowerCase())) {
                    return item;
                }
            }));
        } catch (err) {
            setLoading(false);
        }

    };

    const onSave = async (template: TemplateData) => {
        setLoading(true);
        if (user) {
            if (template.id !== null) {
                api.putTemplateById(user?.subID, template.id, template.name, template.template, template.measurementPoints)
                    .then(() => {
                        getTemplates().then();
                        setLoading(false);
                    })
                    .catch(() => {
                        setLoading(false);
                        console.error('Error upload new template');
                    })
            } else {
                api.postTemplate(user?.subID, template.name, template.template, template.measurementPoints)
                    .then(() => {
                        getTemplates().then();
                        setLoading(false);
                    })
                    .catch(() => {
                        setLoading(false);
                        console.error('Error upload new template');
                    })
            }
        }
    };

    const onClose = async () => {
        setOpenTemplateStepper(false);
        setInitData(null);
    }

    const handleDelete = async (id: number) => {
        if (window.confirm(t("dashboard.deleteConfirm"))) {
            setLoading(true);
            if (window.confirm(t("dashboard.deleteConfirm2")) && user) {
                api.getPruefungen(user?.subID)
                    .then((pruefungen) => {
                        const openExams = pruefungen.filter(pruefung => pruefung.templateID === id && pruefung.pruefungsState !== 2);
                        for (let i = 0; i < openExams.length; i++) {
                            api.deletePruefungById(openExams[i].PruefungsID)
                            .catch(() => {
                                console.error(`Fehler beim Löschen der Prüfung mit ID ${openExams[i].PruefungsID}.`);
                            });
                        }
                        api.deleteTemplateById(user?.subID, id)
                            .then(() => {
                                getTemplates().then();
                                setLoading(false);
                            })
                            .catch(() => {
                                setLoading(false);
                                console.error('Error deleting template');
                            });
                    })
                    .catch(() => {
                        setLoading(false);
                        console.error('Error fetching pruefungen');
                    });
            } else {
            setLoading(false);
            }
        } else {
            setLoading(false);
        }
    }

    const handleOpenTemplateStepper = async (mode: string, template?: TemplateMetadata) => {
        setLoading(true);
        if (user && template && mode === "edit") {
            api.getTemplateData(user.subID, template.templateID)
                .then((data) => {
                    setInitData({
                        ...data,
                        id: template?.templateID,
                        name: template?.templateName,
                    });
                    setLoading(false);
                    setOpenTemplateStepper(true);
                })
                .catch(() => {
                    console.error('Error fetching template data:');
                    setLoading(false);
                })
        }
        else if (mode === "add" && templates.length < 20) {
            setLoading(false);
            setOpenTemplateStepper(true);
        }
        else if (mode === "add" && templates.length > 20) {
            setLoading(false);
            alert("Maximale Anzahl an Templates erreicht!")
        }
    }

    const handleOpenDesigner = (template: TemplateMetadata) => {
        setLoading(true);
        if (user) {
            api.getTemplateData(user?.subID, template.templateID)
                .then(async (data) => {
                    setLoading(false);
                    navigate('designer', { state: { ...data, id: template.templateID, name: template.templateName } });
                })
                .catch(() => {
                    console.error('Error fetching template data');
                    setLoading(false);
                })
        }
    }

    return (
        <Container sx={{ p: 2 }}>
            <Box style={{ display: 'flex', alignItems: 'center' }}>
                <Box style={{
                    flex: 0.2,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                }}>
                    <p>{`${templates.length} / 20`}</p>
                </Box>
                <Box sx={{
                    flex: 0.6,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    borderRadius: 7
                }}>
                    <Box sx={{ flex: 0.05, display: 'flex', justifyContent: 'center' }}>
                        <SearchIcon />
                    </Box>
                    <Box sx={{ flex: 0.95, display: 'flex' }}>
                        <InputBase
                            onChange={handleSearch}
                            fullWidth={true}
                            style={{ width: '98%' }}
                            placeholder={t('dashboard.search') + "..."}
                        />
                    </Box>
                </Box>
                <Box style={{
                    flex: 0.2,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                }}>
                    <Select
                        value={direction}
                        variant="standard"
                        disableUnderline
                        onChange={handleChange}
                    >
                        <MenuItem value={"card"}><ViewColumnIcon /></MenuItem>
                        <MenuItem value={"list"}><ListIcon /></MenuItem>
                    </Select>
                </Box>
            </Box>
            {direction === "card" ?
                <TemplateCards
                    tmpTemplates={tmpTemplates}
                    handleDelete={handleDelete}
                    handleEdit={(template: TemplateMetadata) => handleOpenTemplateStepper("edit", template)}
                    handleOpen={(template: TemplateMetadata) => handleOpenDesigner(template)}
                    loading={loading}
                />
                :
                <TemplateList
                    tmpTemplates={tmpTemplates}
                    handleDelete={handleDelete}
                    handleEdit={(template: TemplateMetadata) => handleOpenTemplateStepper("edit", template)}
                    handleOpen={(template: TemplateMetadata) => handleOpenDesigner(template)}
                    loading={loading}
                />
            }
            <Fab
                onClick={() => handleOpenTemplateStepper("add")}
                style={{ backgroundColor: '#53b2ef', color: "white" }}
                size="small"
                aria-label="add"
                sx={{
                    position: 'fixed',
                    bottom: 16,
                    right: 16
                }}
            >
                <AddBoxOutlinedIcon />
            </Fab>
            <Modal
                open={openTemplateStepper}
                onClose={(_, reason) => {
                    reason !== 'backdropClick' && reason !== 'escapeKeyDown' &&
                        setOpenTemplateStepper(false)
                }}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
                disableEscapeKeyDown
                tabIndex={-1}
            >
                <TemplateStepper
                    templates={templates}
                    blueprints={blueprints}
                    initialData={initData}
                    onSave={onSave}
                    onClose={onClose}
                />
            </Modal>
            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.modal + 1
                }}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Container>
    );
};
export default Dashboard;
